'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'
import type { TypedLocale } from 'payload'
import { getHomeDestination } from '@/utilities/getHomeDestination'
import { NavigationProvider } from '@/components/NavigationProvider'
import TenantProvider from '@/components/TenantProvider'
import { NotFoundTemplate } from '@/components/ui/templates/NotFoundTemplate'
import '@/styles/scss/styles.scss'

type Args = {
  params: Promise<{
    tenant?: string
    locale?: TypedLocale
  }>
}

type Navigation = Parameters<typeof NavigationProvider>[0]['navigation']

export default function NotFound({ params }: Args) {
  const [tenant, setTenant] = useState<string>('')
  const [locale, setLocale] = useState<TypedLocale>('en')
  const [navigation, setNavigation] = useState<Navigation>(null)

  const fetchNavigation = useCallback(
    async (localeArg: TypedLocale, tenantArg: string) => {
      try {
        const res = await fetch(
          `/api/navigation?tenant=${encodeURIComponent(tenantArg)}&locale=${localeArg}`,
        )
        if (!res.ok) return
        const data = (await res.json()) as Navigation
        setNavigation(data)
      } catch {
        setNavigation(null)
      }
    },
    [],
  )

  useEffect(() => {
    let cancelled = false
    Promise.resolve(params).then((resolved) => {
      if (cancelled) return
      if (!resolved) return
      const loc = (resolved.locale ?? 'en') as TypedLocale
      const ten = resolved.tenant ?? ''
      setLocale(loc)
      setTenant(ten)
    })
    return () => {
      cancelled = true
    }
  }, [params])

  useEffect(() => {
    if (tenant) {
      fetchNavigation(locale, tenant)
    }
  }, [tenant, locale, fetchNavigation])

  const homeLink = useMemo(() => getHomeDestination(locale), [locale])

  return (
    <NavigationProvider navigation={navigation}>
      <TenantProvider tenant={tenant}>
        <NotFoundTemplate homeLink={homeLink} />
      </TenantProvider>
    </NavigationProvider>
  )
}
